import {
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { dsDtos } from '@vectronic/openApi-admin';
import { utcMoment } from '@vectronic/util';
import { Subscription, filter } from 'rxjs';
import { CameraScheduleService } from '../../../services/camera-schedule.service';

@Component({
  selector: 'rc-camera-calendar-month-view',
  templateUrl: './camera-calendar-month-view.component.html',
  styleUrls: ['./camera-calendar-month-view.component.scss'],
  imports: [MatIconButton, MatTooltip, MatIcon, NgTemplateOutlet, NgClass, DatePipe],
})
export class CameraCalendarMonthViewComponent implements OnInit, OnDestroy {
  private cameraScheduleService = inject(CameraScheduleService);
  selectedDate = input<Date | null>();
  monthChange = output<Date>();
  dayChange = output<Date>();
  back = output();

  rulesArray = signal<dsDtos.CameraScheduleRule[]>(undefined);
  days = signal<number[]>(undefined);
  dayOffset = signal<number[]>([]);
  fixesOfDays = signal<{ number: number; videoLength: number; photoCount: number }[]>(undefined);

  maxFix = computed(() => {
    return Math.max(...this.fixesOfDays().map((item) => item.number));
  });

  weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  subscription: Subscription;

  constructor() {
    toObservable(this.selectedDate)
      .pipe(
        takeUntilDestroyed(),
        filter((v) => v != null),
      )
      .subscribe(() => {
        this.dayOffset.set(
          Array.from(
            { length: (utcMoment(this.selectedDate(), true).startOf('month').day() + 6) % 7 },
            (_, i) => i + 1,
          ),
        );
        this.days.set(
          Array(utcMoment(this.selectedDate(), true).daysInMonth())
            .fill(0)
            .map((_, i) => i + 1),
        );
        this.fixesOfDays.set(
          this.days()?.map((_, index) => this.numberOfSchedulesAndSizeInDay(index)),
        );
      });
  }

  ngOnInit() {
    this.subscription = this.cameraScheduleService.rules$.subscribe((rules) => {
      this.rulesArray.set(Object.values(rules));
      this.fixesOfDays.set(
        this.days()?.map((value, index) => this.numberOfSchedulesAndSizeInDay(index)),
      );
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  //TODO -> check if it works correctly when we have photo and video together!
  numberOfSchedulesAndSizeInDay(day: number): {
    number: number;
    videoLength: number;
    photoCount: number;
  } {
    let number = 0;
    let videoLength = 0;
    let photoCount = 0;
    this.rulesArray()?.forEach((rule) => {
      let count: number;
      if (this.cameraScheduleService.isNonTimerTrigger(rule)) {
        count = this.cameraScheduleService.numberOfTimespansInDay(
          rule,
          utcMoment(this.selectedDate(), true).set('date', day + 1),
        );
      } else {
        count = this.cameraScheduleService?.numberOfSchedulesInDay(
          rule,
          utcMoment(this.selectedDate(), true).set('date', day + 1),
        );
      }
      number += count;
      if (rule.cameraScheduleRuleType === 'VIDEO') {
        videoLength += rule.size * count;
      } else {
        photoCount += rule.size * count;
      }
    });
    return {
      number,
      videoLength,
      photoCount,
    };
  }

  onClickItem(index: number, isClickable: boolean) {
    if (isClickable)
      this.dayChange.emit(
        utcMoment(this.selectedDate(), true)
          .set('date', index + 1)
          .toDate(),
      );
  }

  onClickBack() {
    this.back.emit();
  }

  onClickPrevious() {
    this.monthChange.emit(utcMoment(this.selectedDate(), true).add('month', -1).toDate());
  }

  onClickNext() {
    this.monthChange.emit(utcMoment(this.selectedDate(), true).add('month', 1).toDate());
  }
  getPercent(average: number) {
    return Math.round((average * 100) / this.maxFix()) || 0;
  }

  getDurationString(value: number): string {
    if (value === 0) return '0s';
    const seconds = Number(value);
    const d = Math.floor(seconds / (3600 * 24));
    const h = Math.floor((seconds % (3600 * 24)) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const dDisplay = d > 0 ? d + 'd ' : '';
    const hDisplay = h > 0 ? h + 'h ' : '';
    const mDisplay = m > 0 ? m + 'm ' : '';
    const sDisplay = s > 0 ? s + 's ' : '';
    return (dDisplay + hDisplay + mDisplay + sDisplay).trim();
  }

  getTooltip(fixRate: { number: number; videoLength: number; photoCount: number }): string {
    if (!fixRate.number) return 'No fixes';
    let string = `${fixRate.number} fixes`;
    if (fixRate.videoLength) string += ` (Videos: ${this.getDurationString(fixRate.videoLength)})`;
    if (fixRate.photoCount) string += ` (Photos: ${fixRate.photoCount})`;
    return string;
  }
}
